<template>
  <div class="root">
    <div class="list-root">
      <div class="head">
        <div class="title">{{ $t('correlationsPage.correlationList.title') }}</div>
        <div class="filter">
          <img src="@/assets/img/icon/filter_list.svg" />
          <select-box
            :items="filterCharacterItems()"
            keyAttr="characterId"
            iconPlaceholder="/img/placeholders/character.png"
            filterMode
            @select="selectFilterCharacter"
            :selected="filterCharacter"
          />
        </div>
      </div>
      <div class="list-container">
        <correlation-list-item
          v-for="item in correlationList"
          :key="item.correlationId"
          :characterA="getCharacterData(item.characterA)"
          :characterB="getCharacterData(item.characterB)"
          :relationshipAtoB="item.relationshipAtoB"
          :relationshipBtoA="item.relationshipBtoA"
          :to="{
            name: 'correlations',
            params: {
              novelId,
              correlationGroupId: selectedCorrelationGroupId,
              correlationId: item.correlationId,
            },
          }"
          :isActive="item.correlationId === selectedCorrelationId"
        />
      </div>
      <button class="create" @click="openForm">{{ $t('correlationsPage.correlationList.createButton') }}</button>
    </div>
    <correlation-form
      v-if="isCreating || selectedCorrelationId"
      class="floating-content"
      :novelId="novelId"
      :selectedCorrelationGroupId="selectedCorrelationGroupId"
      :selectedCorrelationId="selectedCorrelationId"
      :items="characterItems"
      :correlationList="correlationList"
      :correlation="getCorrelationData()"
      :correlationDiagram="correlationDiagram"
      @close="closeForm"
      @fetch="fetchCorrelationList"
      :key="selectedCorrelationId"
    />
  </div>
</template>
<script lang="ts">
import Vue from "vue";
import CorrelationListItem from "@/components/molecules/CorrelationListItem.vue";
import SelectBox from "@/components/molecules/SelectBox.vue";
import CorrelationForm from "@/components/organisms/CorrelationForm.vue";
import { getCharacterKey } from "@/lib/s3";

const filterItem = {
  characterId: "filter-item",
  name: "",
  image: "none",
};

export default Vue.extend({
  components: {
    CorrelationListItem,
    SelectBox,
    CorrelationForm,
  },
  props: {
    novelId: String,
    items: Array as any,
    selectedCorrelationGroupId: String,
    selectedCorrelationId: String,
    characterItems: Array as any,
    correlationDiagram: Object,
  },
  data() {
    return {
      correlationList: this.items as any[],
      isCreating: false,
      filterCharacter: filterItem,
    };
  },
  created() {
    filterItem.name = this.$t('correlationsPage.correlationList.filterPlaceholder') as string;
  },
  methods: {
    openForm() {
      if (!this.isCreating && !this.selectedCorrelationId) {
        this.isCreating = true;
        this.setModified(true);
      }
    },
    closeForm() {
      this.setModified(false);
      this.isCreating = false;
      this.$router.push(
        {
          name: "correlations",
          params: {
            novelId: this.novelId,
            correlationGroupId: this.selectedCorrelationGroupId,
          },
        },
        () => {}
      );
    },
    fetchCorrelationList() {
      this.$emit("fetch");
    },
    getCharacterData(characterId) {
      const character = this.characterItems.find((character) => character.characterId === characterId);
      if (!character) {
        return null;
      }
      character.image = getCharacterKey(this.novelId, character);
      return character;
    },
    getCorrelationData() {
      return this.items.find((correlation) => correlation.correlationId === this.selectedCorrelationId);
    },
    filterCharacterItems() {
      const availableImageItems = this.characterItems.map((item) => {
        // eslint-disable-next-line no-param-reassign
        item.image = getCharacterKey(this.novelId, item);
        return item;
      });
      return [filterItem, ...availableImageItems];
    },
    selectFilterCharacter(item) {
      this.filterCharacter = item;
    },
    filterCorrelation() {
      if (this.filterCharacter.characterId === "filter-item") {
        this.correlationList = this.items;
      } else {
        this.correlationList = this.items.filter(
          (correlation) =>
            correlation.characterA === this.filterCharacter.characterId ||
            correlation.characterB === this.filterCharacter.characterId
        );
      }
    },
  },
  watch: {
    selectedCorrelationGroupId() {
      this.filterCharacter = filterItem;
    },
    filterCharacter() {
      this.filterCorrelation();
    },
    items() {
      this.correlationList = this.items;
      this.filterCorrelation();
    },
    $route(to, from) {
      const isMoveToOtherCorrelationGroup = from.params.correlationGroupId !== to.params.correlationGroupId;
      if (isMoveToOtherCorrelationGroup) {
        this.isCreating = false;
      }
    },
  },
});
</script>
<style lang="scss" scoped>
@import "@/style-common.scss";

.root {
  position: relative;
}

.list-root {
  display: flex;
  flex-direction: column;
  overflow: auto;
  width: 300px;
  flex-shrink: 0;
  height: 100vh;

  @include mq(sp) {
    flex: 1;
    width: 100%;
    min-height: 0;
    overflow: hidden;
  }

  .head {
    position: sticky;
    top: 0;
    z-index: 2;

    display: flex;
    align-items: center;
    justify-content: space-between;
    color: var(--color-text-normal);
    font-weight: var(--font-weight-bold);
    background-color: var(--color-surface-subtle);
    padding: 0 0 0 15px;
    border-bottom: 1px solid var(--color-border);

    .title {
      display: flex;
      align-items: center;
      height: 95%;
      box-sizing: border-box;
    }

    .filter {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      border: none;
      border-radius: 0;
      padding: 4px 4px 4px 8px;
      margin: 0;
      background-color: transparent;
      gap: 8px;

      @include hover-overlay;

      img {
        width: 1.2rem;
        vertical-align: middle;
        flex-shrink: 0;
        filter: brightness(0);

        [data-app-theme="dark"] & {
          filter: brightness(0) invert(1);
        }
      }

      ::v-deep .selectbox {
        border: none;
        background-color: transparent;
        padding: 0;
        width: auto;
        min-width: 120px;

        &.filterMode {
          width: auto;
          min-width: 120px;
        }
      }
    }
  }

  .list-container {
    flex: 1;
    min-height: 0;
    overflow: auto;
  }

  .create {
    position: relative;
    flex-shrink: 0;
    height: 50px;
    font-size: 1.2em;
    font-weight: var(--font-weight-bold);
    color: var(--color-text-on-fixed-bg);
    background-color: var(--color-primary);
    border: none;

    @include hover-overlay;

    @include mq(sp) {
      border-radius: 0;
      margin: 0;
    }
  }
}

.floating-content {
  position: absolute;
  top: 0;
  left: 302px;
  z-index: 101;
}
</style>
